import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { PRACTICE, ASSETS } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";

export default function FinalCTA() {
  const { t } = useT();
  return (
    <section
      id="contact"
      data-testid="final-cta-section"
      className="px-6 md:px-10 lg:px-16 py-24 md:py-32 bg-[var(--primary)] text-[var(--bg)] relative overflow-hidden"
    >
      <div className="absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full bg-[var(--accent)]/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-[var(--accent)]/10 blur-3xl pointer-events-none" />

      <div className="relative grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <div className="font-mono-eyebrow text-[var(--accent)] flex items-center gap-3">
            <span className="w-8 h-px bg-[var(--accent)]" />
            {t("Let's get your smile on")}
          </div>
          <h2 className="font-serif-display mt-5 text-[56px] md:text-[88px] leading-[0.96] tracking-tight">
            {t("Join our")}<br />
            <span className="italic text-[var(--accent)]">{t("dental")}</span> {t("family.")}
          </h2>
          <p className="mt-6 text-[18px] text-[var(--bg)]/75 max-w-xl">
            {t("Schedule your visit online in under a minute, send us a quick note, or call us — we love actually picking up the phone.")}
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a
              data-testid="cta-appointment-btn"
              href={PRACTICE.appointmentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--accent)] text-[var(--primary)] font-medium hover:bg-[var(--accent-hover)] hover:text-[var(--bg)]"
            >
              {t("Request Appointment")} <ArrowUpRight className="w-4 h-4" />
            </a>
            <Link
              data-testid="cta-contact-btn"
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--bg)]/40 text-[var(--bg)] hover:bg-[var(--bg)] hover:text-[var(--primary)]"
            >
              {t("Send a Message")}
            </Link>
            <a
              data-testid="cta-call-btn"
              href={PRACTICE.phoneHref}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--bg)]/40 text-[var(--bg)] hover:bg-[var(--bg)] hover:text-[var(--primary)]"
            >
              <Phone className="w-4 h-4" /> {PRACTICE.phone}
            </a>
          </div>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-6 text-sm">
            <div>
              <div className="text-[var(--bg)]/60 uppercase tracking-[0.22em] text-[11px]">
                {t("Address")}
              </div>
              <a
                href={PRACTICE.mapUrl}
                target="_blank"
                rel="noreferrer"
                className="block mt-2 text-[var(--bg)] hover:text-[var(--accent)]"
                data-testid="footer-address"
              >
                {PRACTICE.address}
              </a>
            </div>
            <div>
              <div className="text-[var(--bg)]/60 uppercase tracking-[0.22em] text-[11px]">
                {t("Languages")}
              </div>
              <div className="mt-2">{PRACTICE.languages.map((l) => t(l)).join(" · ")}</div>
            </div>
            <div>
              <div className="text-[var(--bg)]/60 uppercase tracking-[0.22em] text-[11px]">
                {t("Hours")}
              </div>
              <div className="mt-2 space-y-0.5">
                {PRACTICE.hours.slice(0, 3).map((h) => (
                  <div key={h.d}>
                    <span className="text-[var(--bg)]/60">{t(h.d)}</span> · {t(h.t)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="arch-shape aspect-[3/4] w-full max-w-[440px] mx-auto bg-[var(--surface)]">
            <img
              src={ASSETS.opening}
              alt="Smiling family at Smiles of Loudoun"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
